/**
 * Monte ce composant une fois dans le layout principal — il écoute le
 * useTaskModalStore et ouvre TaskEditModal automatiquement quand un
 * taskId est défini (notifications, deep-links, etc.).
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { parseTaskDeepLink, withoutTaskDeepLink } from '../../lib/tasks/taskDeepLink';
import { useAuthStore } from '../../store/useAuthStore';
import { useTaskModalStore } from '../../store/taskModalStore';
import type { Task, TaskSection } from '../../hooks/queries/useTasks';
import { TaskEditModal } from './TaskEditModal';

export function GlobalTaskModal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const taskId = useTaskModalStore((s) => s.taskId);
  const openTask = useTaskModalStore((s) => s.openTask);
  const closeStore = useTaskModalStore((s) => s.closeTask);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const session = useAuthStore((s) => s.session);

  const urlTaskId = parseTaskDeepLink(searchParams);

  const closeTask = useCallback(() => {
    closeStore();
    if (parseTaskDeepLink(searchParams)) {
      setSearchParams(withoutTaskDeepLink(searchParams), { replace: true });
    }
  }, [closeStore, searchParams, setSearchParams]);

  useEffect(() => {
    if (urlTaskId) openTask(urlTaskId);
  }, [urlTaskId, openTask]);

  const [task, setTask] = useState<Task | null>(null);
  const [sections, setSections] = useState<TaskSection[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!taskId || !isInitialized || !session) {
      setTask(null);
      setSections([]);
      return;
    }
    setLoading(true);
    (async () => {
      const { data: t } = await supabase
        .from('tasks')
        .select('*')
        .eq('id', taskId)
        .maybeSingle();
      if (cancelled) return;
      if (!t) {
        // tâche introuvable → on ferme silencieusement
        closeTask();
        return;
      }
      setTask(t);
      // charge les sections du dossier (pour le sélecteur)
      let sQuery = supabase
        .from('task_sections')
        .select('*')
        .eq('is_archived', false)
        .order('section_order', { ascending: true });
      sQuery = t.dossier_id
        ? sQuery.eq('dossier_id', t.dossier_id)
        : sQuery.is('dossier_id', null);
      const { data: s } = await sQuery;
      if (!cancelled) setSections(s ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [taskId, isInitialized, session, closeTask]);

  if (!taskId || loading) return null;

  return <TaskEditModal task={task} sections={sections} onClose={closeTask} />;
}
